import React from 'react';
import { Panel } from '../../layout/Panel';
import { DataSourceBadge, DATA_SOURCES } from '../../ui/DataSourceBadge';
import { fdiDetailData } from '../../../data/fdiData';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  ResponsiveContainer,
  Cell,
  Tooltip,
  CartesianGrid,
  ComposedChart,
  Line,
  Area,
} from 'recharts';
import { getTooltipStyle } from '../../../hooks/useExpandedStyles';
import { TrendingUp, Globe, Award, Target } from 'lucide-react';

interface RegionalComparisonPanelProps {
  isMinimized?: boolean;
  onMinimize?: () => void;
  isExpanded?: boolean;
}

const COLORS = ['#00d26a', '#00a3ff', '#ffb800', '#a855f7', '#ec4899', '#06b6d4', '#30363d'];

export const RegionalComparisonPanel: React.FC<RegionalComparisonPanelProps> = ({
  isMinimized,
  onMinimize,
  isExpanded = false,
}) => {
  const { regionalComparison } = fdiDetailData;
  const sortedByGrowth = [...regionalComparison].sort((a, b) => b.growth - a.growth);
  const sortedByFDI = [...regionalComparison].sort((a, b) => b.fdi2024 - a.fdi2024);
  const sortedByGDP = [...regionalComparison].sort((a, b) => b.fdiGDP - a.fdiGDP);

  // Calculate UZB rank and stats
  const uzbData = regionalComparison.find((r) => r.country === 'UZB');
  const uzbRankByGrowth = sortedByGrowth.findIndex((r) => r.country === 'UZB') + 1;
  const uzbRankByFDI = sortedByFDI.findIndex((r) => r.country === 'UZB') + 1;
  const avgGrowth = (regionalComparison.reduce((sum, r) => sum + r.growth, 0) / regionalComparison.length).toFixed(1);
  const avgFDI = (regionalComparison.reduce((sum, r) => sum + r.fdi2024, 0) / regionalComparison.length).toFixed(1);

  const expandedContent = (
    <div className="flex flex-col h-full gap-4 md:gap-8">
      {/* Top Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 md:gap-4">
        <div className="bg-terminal-bg rounded-lg p-3 md:p-5">
          <div className="flex items-center gap-1 md:gap-2 text-gray-500 text-xs md:text-sm uppercase mb-1 md:mb-2">
            <Award size={12} className="md:w-[14px] md:h-[14px]" />
            <span>UZB Growth Rank</span>
          </div>
          <div className="text-xl md:text-3xl font-bold text-data-green">#{uzbRankByGrowth}</div>
          <div className="text-xs md:text-sm text-gray-500 mt-1 md:mt-2">Of {regionalComparison.length} peer countries</div>
        </div>
        <div className="bg-terminal-bg rounded-lg p-3 md:p-5">
          <div className="flex items-center gap-1 md:gap-2 text-gray-500 text-xs md:text-sm uppercase mb-1 md:mb-2">
            <TrendingUp size={12} className="md:w-[14px] md:h-[14px]" />
            <span>UZB FDI Growth</span>
          </div>
          <div className="text-xl md:text-3xl font-bold text-data-green">+{uzbData?.growth}%</div>
          <div className="text-xs md:text-sm text-gray-500 mt-1 md:mt-2">vs {avgGrowth}% peer avg</div>
        </div>
        <div className="bg-terminal-bg rounded-lg p-3 md:p-5">
          <div className="flex items-center gap-1 md:gap-2 text-gray-500 text-xs md:text-sm uppercase mb-1 md:mb-2">
            <Globe size={12} className="md:w-[14px] md:h-[14px]" />
            <span>UZB FDI 2024</span>
          </div>
          <div className="text-xl md:text-3xl font-bold text-data-amber">${uzbData?.fdi2024}B</div>
          <div className="text-xs md:text-sm text-gray-500 mt-1 md:mt-2">vs ${avgFDI}B peer avg</div>
        </div>
        <div className="bg-terminal-bg rounded-lg p-3 md:p-5">
          <div className="flex items-center gap-1 md:gap-2 text-gray-500 text-xs md:text-sm uppercase mb-1 md:mb-2">
            <Target size={12} className="md:w-[14px] md:h-[14px]" />
            <span>FDI/GDP Ratio</span>
          </div>
          <div className="text-xl md:text-3xl font-bold text-white">{uzbData?.fdiGDP}%</div>
          <div className="text-xs md:text-sm text-gray-500 mt-1 md:mt-2">Investment intensity</div>
        </div>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
        {/* Growth Comparison Chart */}
        <div className="bg-terminal-bg rounded-lg p-4 md:p-6">
          <h3 className="text-sm md:text-base font-semibold text-gray-200 uppercase tracking-wide mb-3 md:mb-4">
            FDI Growth by Country (%)
          </h3>
          <div className="h-[250px] md:h-[350px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={sortedByGrowth}
                layout="vertical"
                margin={{ top: 10, right: 20, bottom: 10, left: 40 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#1e1e26" horizontal={false} />
                <XAxis
                  type="number"
                  tick={{ fontSize: 10, fill: '#9ca3af' }}
                  tickFormatter={(v) => `+${v}%`}
                />
                <YAxis
                  type="category"
                  dataKey="country"
                  tick={{ fontSize: 10, fill: '#9ca3af' }}
                  width={35}
                />
                <Tooltip
                  contentStyle={getTooltipStyle(true)}
                  formatter={(value: number) => [`+${value}%`, 'FDI Growth']}
                />
                <Bar dataKey="growth" radius={[0, 4, 4, 0]}>
                  {sortedByGrowth.map((entry, index) => (
                    <Cell
                      key={index}
                      fill={entry.country === 'UZB' ? '#00d26a' : '#30363d'}
                    />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* FDI Volume Chart */}
        <div className="bg-terminal-bg rounded-lg p-4 md:p-6">
          <h3 className="text-sm md:text-base font-semibold text-gray-200 uppercase tracking-wide mb-3 md:mb-4">
            FDI Volume 2024 (USD Billions)
          </h3>
          <div className="h-[250px] md:h-[350px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={sortedByFDI}
                layout="vertical"
                margin={{ top: 10, right: 20, bottom: 10, left: 40 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#1e1e26" horizontal={false} />
                <XAxis
                  type="number"
                  tick={{ fontSize: 10, fill: '#9ca3af' }}
                  tickFormatter={(v) => `$${v}B`}
                />
                <YAxis
                  type="category"
                  dataKey="country"
                  tick={{ fontSize: 10, fill: '#9ca3af' }}
                  width={35}
                />
                <Tooltip
                  contentStyle={getTooltipStyle(true)}
                  formatter={(value: number) => [`$${value}B`, 'FDI 2024']}
                />
                <Bar dataKey="fdi2024" radius={[0, 4, 4, 0]}>
                  {sortedByFDI.map((entry, index) => (
                    <Cell
                      key={index}
                      fill={entry.country === 'UZB' ? '#ffb800' : '#30363d'}
                    />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Detailed Comparison Table */}
      <div className="bg-terminal-bg rounded-lg p-3 md:p-6">
        <h3 className="text-sm md:text-base font-semibold text-gray-200 uppercase tracking-wide mb-3 md:mb-4">
          Complete Regional Comparison
        </h3>
        <div className="overflow-auto -mx-3 px-3 md:mx-0 md:px-0">
          <table className="w-full text-xs md:text-sm min-w-[500px]">
            <thead>
              <tr className="text-gray-500 uppercase text-xs md:text-sm tracking-wide border-b border-terminal-border">
                <th className="text-left py-2 md:py-3 px-2 md:px-4 font-medium">Rank</th>
                <th className="text-left py-2 md:py-3 px-2 md:px-4 font-medium">Country</th>
                <th className="text-right py-2 md:py-3 px-2 md:px-4 font-medium">FDI 2024</th>
                <th className="text-right py-2 md:py-3 px-2 md:px-4 font-medium hidden sm:table-cell">FDI/GDP</th>
                <th className="text-right py-2 md:py-3 px-2 md:px-4 font-medium">Growth</th>
                <th className="text-left py-2 md:py-3 px-2 md:px-4 font-medium hidden sm:table-cell">Performance</th>
              </tr>
            </thead>
            <tbody>
              {sortedByGrowth.map((r, index) => (
                <tr
                  key={r.country}
                  className={`border-b border-terminal-border/30 hover:bg-terminal-panel/50 transition-colors ${
                    r.country === 'UZB' ? 'bg-data-amber/10' : ''
                  }`}
                >
                  <td className="py-2 md:py-3 px-2 md:px-4 text-gray-500">#{index + 1}</td>
                  <td className={`py-2 md:py-3 px-2 md:px-4 font-semibold ${r.country === 'UZB' ? 'text-data-amber' : 'text-white'}`}>
                    {r.country}
                    {r.country === 'UZB' && (
                      <span className="ml-1 md:ml-2 text-xs md:text-sm bg-data-amber/20 text-data-amber px-1 md:px-2 py-0.5 rounded">
                        TARGET
                      </span>
                    )}
                  </td>
                  <td className="py-2 md:py-3 px-2 md:px-4 text-right text-white">${r.fdi2024}B</td>
                  <td className="py-2 md:py-3 px-2 md:px-4 text-right text-gray-400 hidden sm:table-cell">{r.fdiGDP}%</td>
                  <td className="py-2 md:py-3 px-2 md:px-4 text-right">
                    <span className={r.country === 'UZB' ? 'text-data-green font-semibold' : 'text-gray-400'}>
                      +{r.growth}%
                    </span>
                  </td>
                  <td className="py-2 md:py-3 px-2 md:px-4 hidden sm:table-cell">
                    <div className="w-20 md:w-32 h-1.5 md:h-2 bg-terminal-border rounded-full overflow-hidden">
                      <div
                        className="h-full rounded-full"
                        style={{
                          width: `${(r.growth / sortedByGrowth[0].growth) * 100}%`,
                          backgroundColor: r.country === 'UZB' ? '#00d26a' : '#6b7280',
                        }}
                      />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Key Insights */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 md:gap-4">
        <div className="bg-terminal-bg rounded-lg p-3 md:p-5 border-l-4 border-data-green">
          <div className="text-xs md:text-sm text-gray-500 uppercase mb-1 md:mb-2">Outperforming</div>
          <div className="text-xl md:text-2xl font-bold text-data-green">
            {regionalComparison.filter((r) => uzbData && r.growth < uzbData.growth).length}
          </div>
          <div className="text-xs md:text-sm text-gray-400 mt-0.5 md:mt-1">Countries below UZB growth</div>
        </div>
        <div className="bg-terminal-bg rounded-lg p-3 md:p-5 border-l-4 border-data-amber">
          <div className="text-xs md:text-sm text-gray-500 uppercase mb-1 md:mb-2">Growth Premium</div>
          <div className="text-xl md:text-2xl font-bold text-data-amber">
            +{(parseFloat(String(uzbData?.growth || 0)) - parseFloat(avgGrowth)).toFixed(0)}%
          </div>
          <div className="text-xs md:text-sm text-gray-400 mt-0.5 md:mt-1">Above peer average</div>
        </div>
        <div className="bg-terminal-bg rounded-lg p-3 md:p-5 border-l-4 border-data-blue">
          <div className="text-xs md:text-sm text-gray-500 uppercase mb-1 md:mb-2">Investment Gap</div>
          <div className="text-xl md:text-2xl font-bold text-data-blue">
            ${(parseFloat(String(uzbData?.fdi2024 || 0)) - parseFloat(avgFDI)).toFixed(1)}B
          </div>
          <div className="text-xs md:text-sm text-gray-400 mt-0.5 md:mt-1">Above peer average</div>
        </div>
      </div>
    </div>
  );

  const compactContent = (
    <div className="flex flex-col h-full">
      {/* Comparison Table */}
      <div className="overflow-auto custom-scrollbar">
        <table className="w-full text-xs">
          <thead className="sticky top-0 bg-terminal-panel">
            <tr className="text-gray-500">
              <th className="text-left py-1 font-medium">CTRY</th>
              <th className="text-right py-1 font-medium">FDI'24</th>
              <th className="text-right py-1 font-medium">/GDP</th>
              <th className="text-right py-1 font-medium">GROWTH</th>
            </tr>
          </thead>
          <tbody>
            {regionalComparison.map((r) => (
              <tr
                key={r.country}
                className={`border-t border-terminal-border/30 ${
                  r.country === 'UZB' ? 'bg-data-amber/10' : ''
                }`}
              >
                <td className={`py-1 font-medium ${r.country === 'UZB' ? 'text-data-amber' : 'text-gray-300'}`}>
                  {r.country}
                  {r.country === 'UZB' && <span className="ml-1 text-xs">#{r.rank}</span>}
                </td>
                <td className="py-1 text-right text-white">${r.fdi2024}B</td>
                <td className="py-1 text-right text-gray-400">{r.fdiGDP}%</td>
                <td className={`py-1 text-right font-medium ${
                  r.country === 'UZB' ? 'text-data-green' : 'text-gray-400'
                }`}>
                  +{r.growth}%
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Growth Bar Chart */}
      <div className="flex-1 min-h-0 mt-2 border-t border-terminal-border pt-2">
        <div className="text-xs text-gray-500 uppercase mb-1">Growth Ranking</div>
        <ResponsiveContainer width="100%" height="85%">
          <BarChart
            data={sortedByGrowth}
            layout="vertical"
            margin={{ top: 0, right: 5, bottom: 0, left: 5 }}
          >
            <XAxis type="number" hide />
            <YAxis
              dataKey="country"
              type="category"
              tick={{ fontSize: 8, fill: '#9ca3af' }}
              axisLine={false}
              tickLine={false}
              width={30}
            />
            <Tooltip
              contentStyle={{
                backgroundColor: '#0a0a0f',
                border: '1px solid #00d26a',
                borderRadius: '6px',
                fontSize: '11px',
                padding: '8px 12px',
                boxShadow: '0 4px 20px rgba(0, 210, 106, 0.15)',
              }}
              labelStyle={{ color: '#ffffff', fontWeight: 'bold', marginBottom: '4px' }}
              formatter={(value: number) => [`+${value}%`, 'FDI Growth']}
              cursor={{ fill: 'rgba(0, 210, 106, 0.1)' }}
            />
            <Bar dataKey="growth" radius={[0, 3, 3, 0]} barSize={10}>
              {sortedByGrowth.map((entry, index) => (
                <Cell
                  key={index}
                  fill={entry.country === 'UZB' ? '#00d26a' : '#30363d'}
                />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Footer */}
      <div className="mt-auto pt-2 border-t border-terminal-border/50 flex items-center justify-between">
        <DataSourceBadge source={DATA_SOURCES.UNCTAD} showLabel size="xs" />
        <span className="text-xs text-gray-500">Dec 2025</span>
      </div>
    </div>
  );

  if (isExpanded) {
    return expandedContent;
  }

  return (
    <Panel
      title="REGIONAL COMPARISON"
      subtitle="FDI PERFORMANCE"
      status="static"
      isMinimized={isMinimized}
      onMinimize={onMinimize}
      expandedContent={expandedContent}
    >
      {compactContent}
    </Panel>
  );
};

export default RegionalComparisonPanel;
